import { primary } from '../../../theme/palette'
import { useMemo, useState } from 'react'
import {
  Avatar, Box, MenuItem, Select, Stack, Typography,
} from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { motion } from 'framer-motion'
import type { User } from '@cmms/types'

const ROLE_COLORS: Record<string, string> = {
  org_admin:  '#0D9488',
  admin:      '#0D9488',
  manager:    '#059669',
  technician: '#D97706',
  viewer:     '#64748B',
}

type SortKey = 'name-asc' | 'name-desc' | 'role' | 'recent'

interface Props {
  users: User[]
  selectedId: string | null
  onSelect: (id: string) => void
}

export function MemberList({ users, selectedId, onSelect }: Props) {
  const [sort, setSort] = useState<SortKey>('name-asc')

  const sorted = useMemo(() => {
    const list = [...users]
    switch (sort) {
      case 'name-asc':  return list.sort((a, b) => a.name.localeCompare(b.name))
      case 'name-desc': return list.sort((a, b) => b.name.localeCompare(a.name))
      case 'role':       return list.sort((a, b) => a.role.localeCompare(b.role))
      case 'recent':     return list.sort((a, b) => {
        const at = a.last_action_at ?? a.last_login_at ?? a.created_at
        const bt = b.last_action_at ?? b.last_login_at ?? b.created_at
        return new Date(bt).getTime() - new Date(at).getTime()
      })
    }
  }, [users, sort])

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', minHeight: 0,
        width: { xs: '100%', md: 360 }, flexShrink: 0,
        borderRight: { xs: 'none', md: '1px solid' },
        borderBottom: { xs: '1px solid', md: 'none' },
        borderColor: 'divider', bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction="row" alignItems="center" spacing={1}
        sx={{ px: 3, py: 1.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>Sort by</Typography>
        <Select
          value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
          size="small" variant="standard" disableUnderline
          sx={{
            fontSize: 12.5, fontWeight: 600, color: 'primary.main',
            '& .MuiSelect-select': { py: 0, pr: '20px !important' },
            '& .MuiSelect-icon': { color: 'primary.main', fontSize: 18 },
          }}
        >
          <MenuItem value="name-asc"  sx={{ fontSize: 13 }}>Name · A to Z</MenuItem>
          <MenuItem value="name-desc" sx={{ fontSize: 13 }}>Name · Z to A</MenuItem>
          <MenuItem value="role"      sx={{ fontSize: 13 }}>Role</MenuItem>
          <MenuItem value="recent"    sx={{ fontSize: 13 }}>Recently active</MenuItem>
        </Select>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {sorted.length}
        </Typography>
      </Stack>

      <Box
        sx={{
          flex: 1, minHeight: 0, overflowY: 'auto', py: 1,
          scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
          '&:hover::-webkit-scrollbar-thumb': {
            background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)',
          },
        }}
      >
        {sorted.map((user, i) => {
          const isSelected = user.id === selectedId
          const roleColor = ROLE_COLORS[user.role] ?? '#64748B'
          const initials = user.name
            .split(' ')
            .map((p) => p[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()

          return (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(0.4, i * 0.012), duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
            >
              <Stack
                direction="row" alignItems="center" spacing={1.5}
                onClick={() => onSelect(user.id)}
                sx={{
                  position: 'relative', px: 2.5, py: 1.25, cursor: 'pointer',
                  transition: 'background-color 120ms ease',
                  bgcolor: isSelected
                    ? primary.selected
                    : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected
                      ? primary.selectedHover
                      : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)',
                  },
                  '&::before': isSelected ? {
                    content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: 3, bgcolor: 'primary.main',
                  } : undefined,
                }}
              >
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar
                    src={user.avatar_url ?? undefined}
                    sx={{ width: 36, height: 36, bgcolor: roleColor, fontSize: 13, fontWeight: 600 }}
                  >
                    {initials}
                  </Avatar>
                  <FiberManualRecordIcon
                    sx={{
                      position: 'absolute', right: -1, bottom: -1,
                      fontSize: 12,
                      color: user.is_active ? '#059669' : '#94A3B8',
                      border: '2px solid', borderColor: 'background.paper', borderRadius: 99,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: 13.5, fontWeight: isSelected ? 600 : 500, letterSpacing: '-0.005em' }}
                    noWrap
                  >
                    {user.name}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.2 }}>
                    <Typography
                      sx={{
                        fontSize: 11, fontWeight: 600,
                        color: roleColor,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {formatRole(user.role)}
                    </Typography>
                    {user.is_client && (
                      <Box component="span" sx={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        px: 0.6, py: 0.05, borderRadius: 0.5,
                        bgcolor: '#7C3AED14', color: '#7C3AED',
                      }}>
                        Client
                      </Box>
                    )}
                    {user.is_vendor && (
                      <Box component="span" sx={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        px: 0.6, py: 0.05, borderRadius: 0.5,
                        bgcolor: '#D9770614', color: '#D97706',
                      }}>
                        Vendor
                      </Box>
                    )}
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>·</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
                      {user.email}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </motion.div>
          )
        })}
        {sorted.length === 0 && (
          <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No members match your filters.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

function formatRole(role: string): string {
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
